import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TextField,
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { useBuild } from '../contexts/BuildContext';

const ChainSection = ({ onUpdate, boostInput = false, field = 'chain' }) => {
  const { fetchCachedList, selectedIndex } = useBuild();
  const [currentChain, setCurrentChain] = useState('');
  const [boost, setBoost] = useState(1);
  const [chainOptions, setChainOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchChains = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchCachedList(`/search/frequent_values/${selectedIndex}/${field}`);
        setChainOptions(data || []);
      } catch (err) {
        console.error('Failed to fetch chains:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchChains();
  }, [fetchCachedList, selectedIndex, field]);

  const handleAction = (actionType) => {
    if (!currentChain) return;
    const updateData = {
      action: actionType,
      filter: 'terms',
      field,
      value: [currentChain],
    };
    if (boostInput) updateData.boost = boost;
    onUpdate(updateData);
    setCurrentChain('');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error">Failed to load chains: {error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Chain</InputLabel>
        <Select
          value={currentChain}
          label="Chain"
          onChange={(e) => setCurrentChain(e.target.value)}
        >
          {chainOptions.map((opt) => (
            <MenuItem key={opt.id} value={opt.id}>
              {opt.id} ({opt.count})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {boostInput && (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Boost"
          type="number"
          value={boost}
          onChange={(e) => setBoost(parseFloat(e.target.value) || 1)}
          inputProps={{ min: 0, step: 0.1 }}
        />
      )}

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleAction('include')}
          disabled={!currentChain}
        >
          <AddIcon />
        </Button>

        {boostInput && (
          <Button
            variant="contained"
            color="success"
            onClick={() => handleAction('boost')}
            disabled={!currentChain}
          >
            <TrendingUpIcon />
          </Button>
        )}

        <Button
          variant="contained"
          color="error"
          onClick={() => handleAction('exclude')}
          disabled={!currentChain}
        >
          <RemoveIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default ChainSection;
